// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseInputAddress, BaseToolbar, BaseUploadDocument, Loading } from '~/components/common';
// Others
import { IFormVenue } from '~/utils/interface/venue';
import { useAppDispatch } from '~/utils/redux/hooks';
import { defaultValuesVenueForm, schema } from './helper';
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING } from '~/utils/constants/common';
import { IBaseAddress, IBaseFile, IBodyUpdate } from '~/utils/interface/common';
import { createVenue, editVenue, getDetailVenue } from '~/thunks/venue/venueThunk';
import {
  compareDataUpdate,
  createFormData,
  formatPhoneNumber,
  getFileNameFromUri,
  removeEmptyObjects,
  removeNonDigits,
} from '~/utils/helper/common';
// Styles, images, icons
import styles from './VenueForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const VenueForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/venue', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: venueId } = useParams();

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormVenue>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesVenueForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormVenue>(defaultValuesVenueForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!venueId) return;

    handleGetDetailVenue(venueId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailVenue = (idCategory: string) => {
    if (!idCategory) return;

    dispatch(getDetailVenue(idCategory))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, venueFile, ...remainingData } = res?.data;

        const body: IFormVenue = {
          ...remainingData,
          name: remainingData.name || EMPTY_STRING,
          email: remainingData.email || EMPTY_STRING,
          address: remainingData.address || EMPTY_STRING,
          city: remainingData.city || EMPTY_STRING,
          state: remainingData.state || EMPTY_STRING,
          zipCode: remainingData.zipCode || EMPTY_STRING,
          country: remainingData.country || EMPTY_STRING,
          lat: remainingData.lat || EMPTY_STRING,
          lng: remainingData.lng || EMPTY_STRING,
          contactName: remainingData.contactName || EMPTY_STRING,
          website: remainingData.website || EMPTY_STRING,
          note: remainingData.note || EMPTY_STRING,
          phoneNumber: formatPhoneNumber(remainingData?.phoneNumber) || EMPTY_STRING,
          venueFile: venueFile ? [{ url: venueFile, file: { name: getFileNameFromUri(venueFile) } as File }] : [],
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleFormVenue = (data: IFormVenue) => {
    venueId ? handleEditVenue(data) : handleAddVenue(data);
  };

  const handleAddVenue = async (data: IFormVenue) => {
    setIsLoading(true);

    const convertData = {
      ...data,
      phoneNumber: removeNonDigits(data?.phoneNumber),
    };

    const formData = await createFormData(removeEmptyObjects(convertData), ['venueFile']);

    dispatch(createVenue(formData))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditVenue = async (data: IFormVenue) => {
    setIsLoading(true);

    const dataChange = compareDataUpdate(initialData, data) as IFormVenue;

    const formatData: IFormVenue = {
      ...dataChange,
      ...(dataChange?.phoneNumber && {
        phoneNumber: removeNonDigits(data?.phoneNumber),
      }),
    };

    const formData = await createFormData(formatData, ['venueFile']);

    const params: IBodyUpdate<FormData> = {
      id: venueId || EMPTY_STRING,
      body: formData,
    };

    dispatch(editVenue(params))
      .unwrap()
      .then(() => {
        handleCancel();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeAddress = (value: IBaseAddress) => {
    setValue('address', value.address ?? EMPTY_STRING, { shouldDirty: true });
    setValue('city', value.city ?? EMPTY_STRING, { shouldDirty: true });
    setValue('state', value.state ?? EMPTY_STRING, { shouldDirty: true });
    setValue('zipCode', value.postalCode ?? EMPTY_STRING, { shouldDirty: true });
    setValue('country', value.country ?? EMPTY_STRING, { shouldDirty: true });
    setValue('lat', String(value?.lat ?? 0), { shouldDirty: true });
    setValue('lng', String(value?.lng ?? 0), { shouldDirty: true });
  };

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleRemoveFile = (file: IBaseFile) => {
    if (venueId && file) setValue('deleteFile', true);
  };
  //#endregion Handle Function

  return (
    <Box id='venueFormPage' className={cx('container')}>
      <BaseToolbar title={t(venueId ? 'form_venue_title_edit' : 'form_venue_title_add')} isBack />

      <Stack className={cx('body')}>
        <Box id='inventoryFormPage' className={cx('boxInfo')}>
          <Typography className={cx('header')}>{t('form_venue_title_venue_detail')}</Typography>

          <Grid2 columnSpacing={6} rowSpacing={'15px'} container>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'name'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_venue_title_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.name?.message}
                      required
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'contactName'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_venue_title_contact_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.contactName?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'email'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_venue_title_email')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.email?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'phoneNumber'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_venue_title_phone_number')}
                      value={value}
                      onChange={(event) => {
                        onChange(formatPhoneNumber(event.target.value));
                      }}
                      errorMessage={errors?.phoneNumber?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'website'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_venue_title_website')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.website?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6} />

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'note'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      multiline
                      rows={9}
                      height={'auto'}
                      label={t('form_venue_title_notes')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.note?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'venueFile'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseUploadDocument
                      label={t('form_venue_title_attachment')}
                      isMultiple={false}
                      uploadedFiles={value}
                      onChange={(files: IBaseFile[]) => onChange(files)}
                      onRemove={handleRemoveFile}
                      errorMessage={errors.venueFile?.message}
                    />
                  );
                }}
              />
            </Grid2>
          </Grid2>

          <Typography className={cx('header')}>{t('form_venue_title_address_info')}</Typography>

          <Grid2 columnSpacing={6} rowSpacing={'15px'} container>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'address'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInputAddress
                      label={t('form_venue_title_address')}
                      value={value}
                      onChange={onChange}
                      onSelectAddress={handleChangeAddress}
                      errorMessage={errors?.address?.message}
                      required
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={6} />

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'city'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_venue_title_city')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'state'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_venue_title_state')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'zipCode'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_venue_title_zip_code')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'country'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_venue_title_country')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>
          </Grid2>
        </Box>
      </Stack>

      <Stack direction={'row'} gap={3} alignItems={'center'} justifyContent={'flex-end'}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCancel}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormVenue)}
          disabled={venueId ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default VenueForm;
